<template>
    <div class="side-menu-wrapper" :class="{ collapsed }">
        <nav class="side-menu" aria-label="Главное меню" v-if="collapsed">
            <ul class="menu-list">
                <li class="menu-item">
                    <router-link
                        to="/projects"
                        class="menu-link"
                        active-class="menu-link-active"
                        :title="collapsed ? 'Проекты' : ''"
                        @click="$emit('navigate')"
                    >
                        <i class="pi pi-file menu-icon"></i>
                    </router-link>
                </li>
                <li class="menu-item">
                    <button
                        type="button"
                        class="menu-link menu-button"
                        :class="{ disabled: !recentProjects.length }"
                        :disabled="!recentProjects.length"
                        :title="recentProjects.length ? 'Недавние: ' + recentProjects[0].name : 'Недавние'"
                        @click="openProject(recentProjects[0])"
                    >
                        <i class="pi pi-clock menu-icon"></i>
                    </button>
                </li>
                <li class="menu-item">
                    <button
                        type="button"
                        class="menu-link menu-button"
                        :class="{ disabled: !favoriteProjects.length }"
                        :disabled="!favoriteProjects.length"
                        :title="favoriteProjects.length ? 'Избранные: ' + favoriteProjects[0].name : 'Избранные'"
                        @click="openProject(favoriteProjects[0])"
                    >
                        <i class="pi pi-star menu-icon"></i>
                    </button>
                </li>
            </ul>
        </nav>

        <nav class="side-menu" aria-label="Главное меню" v-else>
            <div class="menu-section">
                <router-link
                    to="/projects"
                    class="menu-link"
                    active-class="menu-link-active"
                    @click="$emit('navigate')"
                >
                    <i class="pi pi-file menu-icon"></i>
                    <span class="menu-text">Проекты</span>
                </router-link>
            </div>

            <div class="menu-section">
                <div class="menu-section-title">Недавние</div>
                <ul class="project-list" v-if="recentProjects.length">
                    <li v-for="project in recentProjects" :key="'recent-' + project.id">
                        <router-link
                            :to="projectLink(project)"
                            class="project-link"
                            :class="{ 'project-link-active': isCurrentProject(project) }"
                            :title="project.name"
                            @click="$emit('navigate')"
                        >
                            <i class="pi pi-clock project-icon"></i>
                            <span class="project-name">{{ project.name }}</span>
                        </router-link>
                    </li>
                </ul>
                <div v-else class="shortcut-empty">Пока пусто</div>
            </div>

            <div class="menu-section">
                <div class="menu-section-title">Избранные</div>
                <ul class="project-list" v-if="favoriteProjects.length">
                    <li v-for="project in favoriteProjects" :key="'favorite-' + project.id">
                        <router-link
                            :to="projectLink(project)"
                            class="project-link"
                            :class="{ 'project-link-active': isCurrentProject(project) }"
                            :title="project.name"
                            @click="$emit('navigate')"
                        >
                            <i class="pi pi-star project-icon"></i>
                            <span class="project-name">{{ project.name }}</span>
                        </router-link>
                    </li>
                </ul>
                <div v-else class="shortcut-empty">Пока пусто</div>
            </div>

            <div class="menu-section menu-section-secondary">
                <router-link
                    to="/projects"
                    class="menu-link menu-link-compact"
                    active-class="menu-link-active"
                    @click="$emit('navigate')"
                >
                    <i class="pi pi-folder-open menu-icon"></i>
                    <span class="menu-text">Все проекты</span>
                </router-link>
            </div>
        </nav>

        <div class="side-menu-bottom">
            <button
                type="button"
                class="bottom-action logout-action"
                :title="collapsed ? 'Выйти' : ''"
                @click="logoff"
            >
                <i class="pi pi-sign-out bottom-action-icon"></i>
                <span v-if="!collapsed">Выйти</span>
            </button>
            <div v-if="collapsed" class="mt-3">
                <a href="https://t.me/gramotacafe" target="_blank" class="support-icon-link" title="Написать в поддержку">
                    <i class="pi pi-question-circle"></i>
                </a>
            </div>
            <div v-if="!collapsed" class="mt-5">
                <a href="https://t.me/gramotacafe" target="_blank" class="support-link">
                    Написать в поддержку
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import {user} from '@/libs/auth'
import EventBus from '@/libs/event-bus'
import events from '@/libs/events'
import projectShortcuts from '@/libs/projectShortcuts'

export default {
    name: 'SideMenu',
    emits: ['navigate'],
    props: {
        collapsed: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            recentProjects: [],
            favoriteProjects: []
        }
    },
    methods: {
        refreshShortcuts() {
            this.recentProjects = projectShortcuts.getRecentProjects();
            this.favoriteProjects = projectShortcuts.getFavoriteProjects();
        },
        projectLink(project) {
            return project ? { name: 'project_edit', params: { project_id: project.id } } : '/projects';
        },
        openProject(project) {
            if (!project) {
                return;
            }

            this.$router.push(this.projectLink(project));
            this.$emit('navigate');
        },
        isCurrentProject(project) {
            return project && String(this.$route.params.project_id) === String(project.id);
        },
        logoff() {
            user.logoff();
        }
    },
    mounted() {
        this.refreshShortcuts();
        EventBus.$on(events.PROJECT_SHORTCUTS_UPDATED, this.refreshShortcuts);
    },
    beforeUnmount() {
        EventBus.$off(events.PROJECT_SHORTCUTS_UPDATED, this.refreshShortcuts);
    }
}
</script>

<style scoped>
.side-menu-wrapper {
    padding: 0.65rem 0.7rem;
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    box-sizing: border-box;
    text-align: left;
    overflow: hidden;
}

.side-menu {
    max-height: calc(100% - 6.5rem);
    margin-bottom: 0;
    padding-bottom: 0;
    overflow-y: auto;
    scrollbar-width: thin;
}

.collapsed .side-menu {
    max-height: calc(100% - 5.25rem);
}

.menu-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.menu-item {
    margin-bottom: 0.5rem;
}

.menu-section {
    margin-bottom: 0.95rem;
}

.menu-section-secondary {
    padding-top: 0.1rem;
}

.menu-section-title {
    margin: 0 0 0.35rem 0.45rem;
    color: rgba(255, 255, 255, 0.62);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.menu-link {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.62rem 0.75rem;
    color: white;
    text-decoration: none;
    border-radius: 0.4rem;
    transition: all 0.2s ease;
    background: transparent;
    border: none;
    width: 100%;
    text-align: left;
    font-size: 0.96rem;
}

.menu-button {
    cursor: pointer;
}

.collapsed .menu-link {
    justify-content: center;
    padding: 0.68rem 0.35rem;
}

.menu-link:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    text-decoration: none;
}

.menu-link:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
    background-color: rgba(255, 255, 255, 0.1);
}

.menu-link-active {
    background-color: rgba(255, 255, 255, 0.16);
    color: white;
}

.menu-link-compact {
    padding-top: 0.56rem;
    padding-bottom: 0.56rem;
    font-size: 0.92rem;
}

.menu-link.disabled,
.menu-link:disabled {
    cursor: default;
    opacity: 0.45;
}

.menu-link.disabled:hover,
.menu-link:disabled:hover {
    background-color: transparent;
}

.menu-icon {
    font-size: 1rem;
    width: 1.15rem;
    text-align: center;
    flex: 0 0 1.15rem;
}

.menu-text {
    font-weight: 500;
    white-space: nowrap;
}

.menu-link:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 2px;
}

.project-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.project-list li {
    margin-bottom: 0.18rem;
}

.project-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    padding: 0.44rem 0.55rem;
    color: rgba(255, 255, 255, 0.88);
    text-decoration: none;
    border-radius: 0.35rem;
    font-size: 0.86rem;
    line-height: 1.2;
    transition: all 0.2s ease;
}

.project-link:hover,
.project-link:focus {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    text-decoration: none;
}

.project-link:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 2px;
}

.project-link-active {
    background-color: rgba(255, 255, 255, 0.14);
    color: white;
}

.project-icon {
    flex: 0 0 0.95rem;
    font-size: 0.76rem;
    opacity: 0.78;
    text-align: center;
}

.project-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.shortcut-empty {
    padding: 0.28rem 0.55rem;
    color: rgba(255, 255, 255, 0.58);
    font-size: 0.83rem;
}

.side-menu-bottom {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 0 0.7rem 0.75rem;
    text-align: left;
}

.bottom-action {
    display: flex;
    align-items: center;
    gap: 0.58rem;
    width: 100%;
    min-height: 2.25rem;
    padding: 0.55rem 0.7rem;
    color: white;
    background: transparent;
    border: 0;
    border-radius: 0.4rem;
    cursor: pointer;
    font: inherit;
    font-size: 0.92rem;
    line-height: 1.2;
    text-align: left;
    transition: background-color 0.2s ease;
}

.bottom-action:hover,
.bottom-action:focus {
    background-color: rgba(255, 255, 255, 0.1);
}

.bottom-action:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 2px;
}

.bottom-action-icon {
    width: 1.15rem;
    flex: 0 0 1.15rem;
    text-align: center;
}

.collapsed .side-menu-bottom {
    padding-left: 0.45rem;
    padding-right: 0.45rem;
    text-align: center;
}

.collapsed .bottom-action {
    justify-content: center;
    padding-left: 0.35rem;
    padding-right: 0.35rem;
}

.support-icon-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    color: white;
    text-decoration: none;
    border-radius: 0.45rem;
}

.support-icon-link:hover,
.support-icon-link:focus {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
}

.support-link {
    display: inline-flex;
    max-width: 100%;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.88rem;
    line-height: 1.25;
    text-decoration: underline;
    text-underline-offset: 0.18rem;
}

.support-link:hover,
.support-link:focus {
    color: white;
}
</style>
